import { type FC, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Breadcrumbs } from './Breadcrumbs';
import { buildBreadcrumbs, type Crumb } from '@/lib/breadcrumbs';

/**
 * Small presentational helpers used across Sprint 2 pages.
 *
 *   PageHeader  — title + subtitle + right-side actions
 *   Card        — simple opaque card container (data-surface)
 *   GlassCard   — same shape but glass (sparingly)
 *   EmptyState  — consistent "nothing here" state
 *   ErrorState  — consistent error state
 */

export const PageHeader: FC<{
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  className?: string;
  /** Override the current-page breadcrumb label (defaults to `title`). */
  breadcrumbLabel?: string;
  /** Fully override the trail. Pass [] to suppress breadcrumbs entirely. */
  breadcrumbs?: Crumb[];
}> = ({ title, subtitle, actions, className, breadcrumbLabel, breadcrumbs }) => {
  const { pathname } = useLocation();
  // Auto-derive from the route hierarchy; empty on shallow pages → renders nothing.
  const crumbs = breadcrumbs ?? buildBreadcrumbs(pathname, breadcrumbLabel ?? title);
  // The page title now lives in the top app bar (AppShell), so PageHeader only
  // carries the breadcrumb trail, an optional subtitle, and page actions.
  if (crumbs.length === 0 && !subtitle && !actions) return null;
  return (
    <div className={cn('mb-1', className)}>
      {crumbs.length > 0 && (
        <div className="mb-1.5">
          <Breadcrumbs items={crumbs} />
        </div>
      )}
      <header className="flex flex-wrap items-center justify-between gap-3">
        {subtitle ? (
          <p className="min-w-0 text-caption text-text-tertiary max-w-2xl leading-relaxed">{subtitle}</p>
        ) : (
          <span className="min-w-0" />
        )}
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </header>
    </div>
  );
};

export const Card: FC<{ children: ReactNode; className?: string; padding?: boolean }> = ({
  children,
  className,
  padding = true,
}) => (
  <div className={cn('data-surface', padding && 'p-5', className)}>{children}</div>
);

export const GlassCard: FC<{ children: ReactNode; className?: string; padding?: boolean }> = ({
  children,
  className,
  padding = true,
}) => (
  <div className={cn('liquid-glass-strong rounded-glass-lg', padding && 'p-3', className)}>
    {children}
  </div>
);

export const EmptyState: FC<{
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}> = ({ title, description, action, icon }) => (
  <div className="flex flex-col items-center text-center py-5 px-3">
    <div className="w-12 h-12 rounded-full bg-slate-900/5 text-text-muted flex items-center justify-center">
      {icon ?? <Inbox className="w-5 h-5" />}
    </div>
    <h3 className="mt-3 text-heading font-semibold text-text-primary">{title}</h3>
    {description && (
      <p className="mt-0.5 text-caption text-text-tertiary max-w-md leading-relaxed">{description}</p>
    )}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

export const ErrorState: FC<{
  title?: string;
  description?: string;
  action?: ReactNode;
}> = ({
  title = 'Something went wrong',
  description = 'We could not load this section. Please try again.',
  action,
}) => (
  <div className="flex flex-col items-center text-center py-5 px-3">
    <div className="w-12 h-12 rounded-full bg-red-500/10 text-red-700 flex items-center justify-center">
      <AlertTriangle className="w-5 h-5" />
    </div>
    <h3 className="mt-3 text-heading font-semibold text-text-primary">{title}</h3>
    <p className="mt-0.5 text-caption text-text-tertiary max-w-md leading-relaxed">{description}</p>
    {action && <div className="mt-3">{action}</div>}
  </div>
);

export const SectionLabel: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'text-caption font-semibold uppercase tracking-[0.08em] text-on-surface-variant',
      className,
    )}
  >
    {children}
  </div>
);
